import { Box, Chip, Stack, Typography } from '@mui/material';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { GlassPanel } from '../../../shared/components/GlassPanel';
import { formatAge } from '../../../shared/components/relativeTime';
import type { DocumentType } from '../../../core/types/data-models';
import { AssistantHeaderBlock } from './AssistantHeaderBlock';
import { RecommendedArtifactRow } from './RecommendedDocumentCard';
import { BlockedCategoryNotice, RemainingDocTypesRow } from './RemainingDocTypes';
import type { AssistantRoleData } from './assistantRoles';

interface AssistantRoleCardProps extends AssistantRoleData {
  inReviewCountByDoc: Map<number, number>;
  canGenerate: boolean;
  onOpenDocument: (id: number) => void;
  onGenerateRecommended: (docType: DocumentType) => void;
}

/** Column layout: one card per role — the header block above a compact "Linked Artifacts" list. */
export function AssistantRoleCard({
  role,
  relatedDocs,
  recommended,
  blocked,
  remaining,
  inReviewCountByDoc,
  canGenerate,
  onOpenDocument,
  onGenerateRecommended,
}: AssistantRoleCardProps) {
  return (
    <GlassPanel
      hover
      sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      <AssistantHeaderBlock
        role={role}
        hasDocuments={relatedDocs.length > 0}
        docCount={relatedDocs.length}
        sx={{ height: 200 }}
      />

      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography
          sx={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            color: 'text.disabled',
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            display: 'block',
            mb: 1,
          }}
        >
          Linked Artifacts
        </Typography>
        {relatedDocs.length === 0 && recommended.length === 0 && (
          blocked ? (
            <BlockedCategoryNotice blocked={blocked} sx={{ display: 'block', mb: 0.75 }} />
          ) : remaining === 0 ? (
            <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
              No documents generated yet
            </Typography>
          ) : null
        )}
        {(relatedDocs.length > 0 || recommended.length > 0 || remaining > 0) && (
          <Stack spacing={0.75} sx={{ maxHeight: 176, overflowY: 'auto' }}>
            {relatedDocs.map((doc) => {
              const reviewCount = inReviewCountByDoc.get(doc.id) ?? 0;
              return (
                <Stack
                  key={doc.id}
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  onClick={() => onOpenDocument(doc.id)}
                  sx={{
                    px: 1,
                    py: 0.5,
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    cursor: 'pointer',
                    transition: 'border-color var(--transition)',
                    '&:hover': { borderColor: 'var(--color-primary-line)' },
                  }}
                >
                  <Typography variant="caption" sx={{ flex: 1, minWidth: 0 }} noWrap>
                    {doc.name}
                  </Typography>
                  {reviewCount > 0 ? (
                    <Chip
                      label={reviewCount}
                      size="small"
                      color="warning"
                      sx={{ height: 16, fontSize: 9, minWidth: 20 }}
                    />
                  ) : (
                    <TaskAltIcon sx={{ fontSize: 13 }} color="success" />
                  )}
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ whiteSpace: 'nowrap', fontSize: 10 }}
                  >
                    {formatAge(doc.updatedAt)}
                  </Typography>
                </Stack>
              );
            })}
            {recommended.map(({ docType: dt }) => (
              <RecommendedArtifactRow
                key={`rec-${dt.id}`}
                docType={dt}
                canGenerate={canGenerate}
                onGenerate={() => onGenerateRecommended(dt)}
              />
            ))}
            {remaining > 0 && (
              <RemainingDocTypesRow count={remaining} roleColor={role.color ?? '#6366f1'} />
            )}
          </Stack>
        )}
      </Box>
    </GlassPanel>
  );
}
